import { useEffect, useState, type FormEvent } from "react";
import { KeyRound, RefreshCw, X } from "lucide-react";
import { Modal } from "@veorest/ui";
import { useI18n } from "../../i18n";
import { generatePassword } from "../../lib/partnerAccounts";

interface ResetPasswordModalProps {
  open: boolean;
  busy?: boolean;
  /** Store or driver name, shown so the Partner confirms the right account. */
  targetLabel: string;
  /** Locked login id (usernames are immutable after activation). */
  username?: string | null;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
}

const MIN_LENGTH = 8;

/**
 * Partner-initiated password reset. A hash can never be shown again, so this
 * issues a NEW password (typed or generated) and the caller shows it once.
 */
export function ResetPasswordModal({
  open,
  busy,
  targetLabel,
  username,
  onClose,
  onSubmit,
}: ResetPasswordModalProps) {
  const { t } = useI18n();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setPassword("");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const handleGenerate = () => {
    setPassword(generatePassword());
    setError("");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_LENGTH) {
      setError(t("accounts.passwordMin"));
      return;
    }
    setError("");
    await onSubmit(password);
  };

  return (
    <Modal open={open} onClose={onClose} panelClassName="max-w-md">
      <div className="flex max-h-[calc(100vh-32px)] flex-col overflow-y-auto">
        <div className="flex items-start justify-between border-b border-[var(--border-subtle)] px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-wider text-[var(--text-muted)]">
              {t("accounts.eyebrow")}
            </p>
            <h2 className="mt-1 truncate text-lg font-extrabold text-[var(--text-primary)]">
              {t("accounts.resetTitle")}
            </h2>
            <p className="mt-1 truncate text-xs font-semibold text-[var(--text-secondary)]">
              {targetLabel}
              {username ? ` · @${username}` : ""}
            </p>
          </div>
          <button
            type="button"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--border-subtle)]"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 px-5 py-4">
          <p className="text-xs leading-5 text-[var(--text-muted)]">
            {t("accounts.resetHint")}
          </p>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("accounts.newPassword")}
            </span>
            <div className="flex items-center gap-2">
              <input
                className="input-base w-full"
                type="text"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                required
                minLength={MIN_LENGTH}
                autoComplete="new-password"
                spellCheck={false}
                dir="ltr"
                placeholder={t("accounts.passwordPlaceholder")}
              />
              <button
                type="button"
                onClick={handleGenerate}
                className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl border border-[var(--border-subtle)] px-3 text-xs font-bold text-[var(--accent)]"
                title={t("accounts.generate")}
              >
                <RefreshCw className="h-4 w-4" />
                {t("accounts.generate")}
              </button>
            </div>
            <span className="text-[11px] text-[var(--text-muted)]">
              {t("accounts.passwordMin")}
            </span>
          </label>

          {error ? (
            <p className="text-xs font-bold text-[var(--danger)]">{error}</p>
          ) : null}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={onClose}
              disabled={busy}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={busy}>
              <KeyRound className="h-4 w-4" />
              {busy ? t("common.loading") : t("accounts.resetCta")}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
